import { useEffect, useMemo, useState } from "react";
import { Calculator, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { api } from "../lib/api";

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return isFinite(n) ? n : null;
};

export const ChargeCalcDialog = ({ trip, prefill = {}, children }) => {
  const [open, setOpen] = useState(false);
  const [garage, setGarage] = useState(null);
  const [sel, setSel] = useState("trip");
  const [kw, setKw] = useState(prefill.kw ?? 50);
  const [minutes, setMinutes] = useState(prefill.minutes ?? 30);
  const [soc, setSoc] = useState(prefill.soc ?? "");

  useEffect(() => {
    if (!open || garage !== null) return;
    api.get("/garage")
      .then((r) => setGarage((r.data || []).filter((g) => g.vehicle?.consumption_kwh_100km)))
      .catch(() => setGarage([]));
  }, [open, garage]);

  const options = useMemo(() => {
    const list = [];
    if (trip?.vehicle?.consumption_kwh_100km) {
      list.push({ key: "trip", label: `${trip.vehicle.brand || ""} ${trip.vehicle.model || ""} — deze trip`.trim(), v: trip.vehicle });
    }
    (garage || []).forEach((g, idx) => {
      const name = g.nickname || `${g.vehicle.brand || ""} ${g.vehicle.model || ""}`.trim();
      if (list.some((o) => o.v.brand === g.vehicle.brand && o.v.model === g.vehicle.model && !g.nickname)) return;
      list.push({ key: `g${idx}`, label: name, v: g.vehicle });
    });
    return list;
  }, [trip, garage]);

  const cur = options.find((o) => o.key === sel) || options[0];
  const v = cur?.v;

  const kwN = num(kw);
  const socN = num(soc);
  const minN = Math.max(num(minutes) || 0, 0);
  const powerAt = (p, s) => (s == null || s <= 80 ? p : p * Math.max(1 - 0.85 * ((s - 80) / 20), 0.15));
  let res = null;
  if (v && kwN > 0) {
    const eff = v.max_charge_kw ? Math.min(kwN, v.max_charge_kw) : kwN;
    const kmPerHour = (powerAt(eff, socN) / v.consumption_kwh_100km) * 100;
    let kwhAdded = 0;
    let endSoc = null;
    let full = false;
    let taper = false;
    if (socN != null && v.battery_kwh) {
      let s = Math.min(Math.max(socN, 0), 100);
      let t = 0;
      while (t < minN && s < 99.95) {
        const room = ((100 - s) / 100) * v.battery_kwh;
        const add = Math.min(powerAt(eff, s) * (0.5 / 60), room);
        kwhAdded += add;
        s += (add / v.battery_kwh) * 100;
        if (s > 80) taper = true;
        t += 0.5;
      }
      full = s >= 99.95;
      endSoc = s;
    } else {
      kwhAdded = eff * (minN / 60);
    }
    res = {
      eff, kmPerHour, kwhAdded, endSoc, full, taper,
      limited: Boolean(v.max_charge_kw && kwN > v.max_charge_kw),
      kmAdded: (kwhAdded / v.consumption_kwh_100km) * 100,
    };
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-md rounded-3xl" data-testid="charge-calc-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Calculator className="w-4 h-4 text-primary" /> Laad-rekenaar
          </DialogTitle>
        </DialogHeader>
        {options.length === 0 ? (
          <p className="text-sm text-muted-foreground">Geen EV met verbruiksgegevens gevonden — voeg een wagen toe in "Mijn Garage".</p>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold text-muted-foreground block mb-1">Wagen</label>
              <select data-testid="charge-calc-vehicle-select" value={cur?.key} onChange={(e) => setSel(e.target.value)}
                className="w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none cursor-pointer">
                {options.map((o) => <option key={o.key} value={o.key}>{o.label} ({o.v.consumption_kwh_100km} kWh/100km)</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] font-semibold text-muted-foreground block mb-1">Vermogen paal (kW)</label>
                <input data-testid="charge-calc-kw-input" type="text" inputMode="decimal" value={kw} onChange={(e) => setKw(e.target.value)}
                  placeholder="bv. 47"
                  className="w-full bg-secondary rounded-xl px-3 py-2 text-sm font-mono font-bold outline-none focus:ring-2 focus:ring-[#B5838D]" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-muted-foreground block mb-1">Batterij nu (%) — optioneel</label>
                <input data-testid="charge-calc-soc-input" type="text" inputMode="numeric" value={soc} onChange={(e) => setSoc(e.target.value)}
                  placeholder="bv. 35"
                  className="w-full bg-secondary rounded-xl px-3 py-2 text-sm font-mono font-bold outline-none focus:ring-2 focus:ring-[#B5838D]" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-muted-foreground block mb-1">
                Laadtijd: <span className="font-mono font-bold text-foreground">{minN} min</span>
              </label>
              <input data-testid="charge-calc-minutes-input" type="range" min="5" max="180" step="5" value={minN}
                onChange={(e) => setMinutes(e.target.value)} className="w-full accent-[#2D6A4F]" />
            </div>
            {res ? (
              <div className="bg-primary/5 rounded-2xl p-4 space-y-1.5" data-testid="charge-calc-result">
                <p className="font-mono font-extrabold text-2xl text-primary" data-testid="charge-calc-kmh">
                  ±{Math.round(res.kmPerHour)} km <span className="text-sm font-bold text-muted-foreground">per uur laden{socN != null && socN > 80 ? ` (bij ${Math.round(socN)}%)` : ""}</span>
                </p>
                <p className="text-sm font-semibold" data-testid="charge-calc-minutes-result">
                  Na {minN} min: <span className="font-mono font-bold text-primary">±{Math.round(res.kmAdded)} km</span> erbij
                  <span className="text-xs text-muted-foreground font-mono"> · {res.kwhAdded.toFixed(1)} kWh{res.endSoc != null ? ` · ${Math.round(socN)}% → ${Math.round(res.endSoc)}%` : ""}</span>
                </p>
                {res.full && <p className="text-xs text-emerald-700 font-semibold" data-testid="charge-calc-full-note">Batterij vol vóór de laadtijd om is.</p>}
                {res.taper && !res.full && (
                  <p className="text-xs text-muted-foreground" data-testid="charge-calc-taper-note">Boven 80% bouwt het laadvermogen af — dit is meegerekend.</p>
                )}
                {res.limited && (
                  <p data-testid="charge-calc-limited-warning" className="text-xs text-red-700 bg-red-500/10 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>De paal levert {kwN} kW, maar deze wagen laadt max <b>{v.max_charge_kw} kW</b> — we rekenen met {res.eff} kW.</span>
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Vul het laadvermogen van de paal in (kW) om te rekenen.</p>
            )}
            <p className="text-[10px] text-muted-foreground">Bruto berekening o.b.v. gemiddeld verbruik ({v?.consumption_kwh_100km} kWh/100km). Vul je batterij-% in, dan rekenen we boven 80% met een afbouwende laadcurve.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
